import React, { useCallback, useEffect, useState } from "react";
import {
  Grid,
  IconButton,
  Tooltip,
  Typography,
  Avatar,
  Switch,
  Box,
  Stack,
  Button,
  Chip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  DialogActions,
  DialogContent,
  Pagination,
  CircularProgress,
  Paper,
  InputBase,
  Divider,
} from "@mui/material";
import { useTheme, alpha } from "@mui/material/styles";
import { toast } from "react-toastify";

import {
  CollapsibleSection,
  ConfirmDialog,
  MainCard,
  MuiTable,
  StatusTag,
  Autocomplete,
  ValidatorForm,
} from "components";
import CustomDialog from "components/Dialog";

import VisibilityIcon from "@mui/icons-material/Visibility";
import { Delete } from "@mui/icons-material";
import EmailOutlinedIcon from "@mui/icons-material/EmailOutlined";
import PhoneOutlinedIcon from "@mui/icons-material/PhoneOutlined";
import CalendarTodayOutlinedIcon from "@mui/icons-material/CalendarTodayOutlined";
import ArticleOutlinedIcon from "@mui/icons-material/ArticleOutlined";
import SearchIcon from "@mui/icons-material/Search";
import MenuIcon from "@mui/icons-material/Menu";
import FilterAltOutlinedIcon from "@mui/icons-material/FilterAltOutlined";
import FilterAltOffIcon from "@mui/icons-material/FilterAltOff";

import JournalistsServices from "services/JournalistsServices";
import TopicServices from "services/TopicServices";
import { dateParse, dateTimeParse, scrollToTop } from "Utils";
import InfoRequestCard from "pages/Dashboard/components/InfoRequestCard";
import { useTranslation } from "react-i18next";

const AllJournalists = () => {
  const { t } = useTranslation();
  const theme = useTheme();
  const isLight = theme.palette.mode === "light";

  const [loading, setLoading] = useState(false);
  const [data, setData] = useState([]);
  const [open, setOpen] = useState(false);
  const [currentJournalist, setCurrentJournalist] = useState(null);

  // Filter view state
  const [filterView, setFilterView] = useState(false);
  const [searchValue, setSearchValue] = useState("");
  const [selectedStatus, setSelectedStatus] = useState(null);
  const [selectedType, setSelectedType] = useState(null);

  // New state for user type change dialog
  const [userTypeDialogOpen, setUserTypeDialogOpen] = useState(false);
  const [selectedUserType, setSelectedUserType] = useState("");
  const [userTypeChangeLoading, setUserTypeChangeLoading] = useState(false);
  const [userToChangeType, setUserToChangeType] = useState(null);

  // New state for topics section
  const [topicsData, setTopicsData] = useState([]);
  const [loadingTopics, setLoadingTopics] = useState(false);
  const [topicsPage, setTopicsPage] = useState(1);
  const [topicsLimit] = useState(4);
  const [topicsTotalPages, setTopicsTotalPages] = useState(1);
  const [topicsTotalItems, setTopicsTotalItems] = useState(0);

  const [filters, setFilters] = useState({
    page: 1,
    limit: 20,
    search: null,
    type: null,
    status: null,
    order: [["updatedAt", "DESC"]],
  });
  const [totalItems, setTotalItems] = useState(0);

  // Debounced search effect
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (searchValue.length >= 3) {
        setFilters((prev) => ({
          ...prev,
          search: searchValue,
          page: 1,
        }));
      } else if (searchValue.length === 0) {
        setFilters((prev) => ({
          ...prev,
          search: null,
          page: 1,
        }));
      }
    }, 500); // 500ms delay for debouncing

    return () => clearTimeout(timeoutId);
  }, [searchValue]);

  const getData = useCallback(async () => {
    setLoading(true);
    const requestFilters = { ...filters };

    // If type is selected, send as array, otherwise send all types
    if (requestFilters.type === null) {
      requestFilters.type = ["user", "journalist", "informant"];
    } else {
      requestFilters.type = [requestFilters.type];
    }

    // If status is selected, send it as string, otherwise don't send
    if (requestFilters.status === null) {
      delete requestFilters.status;
    }

    const response = await JournalistsServices.get(requestFilters);
    if (response.success) {
      setData(response?.data?.view?.data || []);
      setTotalItems(response?.data?.view?.totalItems || 0);
    }
    setLoading(false);
  }, [filters]);

  useEffect(() => {
    getData();
  }, [getData]);

  // Handle manual search (for search button and Enter key)
  const handleSearch = () => {
    if (searchValue.length >= 3 || searchValue.length === 0) {
      setFilters((prev) => ({
        ...prev,
        search: searchValue || null,
        page: 1,
      }));
    }
  };

  // Handle status filter change
  const handleStatusChange = (event, newValue) => {
    setSelectedStatus(newValue);
    setFilters((prev) => ({
      ...prev,
      status: newValue?.value || null,
      page: 1,
    }));
  };

  // Handle type filter change
  const handleTypeChange = (event, newValue) => {
    setSelectedType(newValue);
    setFilters((prev) => ({
      ...prev,
      type: newValue?.value || null,
      page: 1,
    }));
  };

  // Clear all filters
  const handleClearFilters = () => {
    setSearchValue("");
    setSelectedStatus(null);
    setSelectedType(null);
    setFilters({
      page: 1,
      limit: 20,
      search: null,
      type: null,
      status: null,
      order: [["updatedAt", "DESC"]],
    });
  };

  const setPage = (page) => {
    setFilters((prevFilters) => ({
      ...prevFilters,
      page: page,
    }));
  };

  const setPageSize = (pageSize) => {
    setFilters((prevFilters) => ({
      ...prevFilters,
      page: 1,
      limit: pageSize,
    }));
  };

  const handleSubmit = async (id) => {
    try {
      const response = await JournalistsServices.delete(id);
      if (response.success) {
        toast.success(response.message || t("messages.success.dataDeleted"));
      } else {
        toast.error(response.message || t("messages.error.deleteFailed"));
      }

      if (response.success) {
        setCurrentJournalist(null);
        getData();
        scrollToTop();
      }
    } catch (error) {
      toast.error(t("messages.error.unexpectedError"));
    } finally {
      // noop
    }
  };

  const handleOpenDialog = (id) => {
    ConfirmDialog({
      type: "error",
      title: t("messages.confirm.deleteTitle", {
        item: t("journalists.journalist"),
      }),
      description: t("messages.confirm.deleteMessage", {
        item: t("journalists.journalist"),
      }),
      buttons: [
        {
          label: t("common.cancel"),
          className: "custom-confirm-dialog__btn--cancel",
        },
        {
          label: t("common.delete"),
          className: "custom-confirm-dialog__btn--danger",
          onClick: () => handleSubmit(id),
        },
      ],
    });
  };

  // Fix: Toggle status based on Switch checked, not the string "ACTIVE"
  const changeStatus = async (id, checked) => {
    const newStatus = checked ? "Active" : "Inactive";
    const statusText = checked ? t("common.active") : t("common.inactive");

    ConfirmDialog({
      type: checked ? "success" : "warning",
      title: t("messages.confirm.statusChangeTitle", { status: statusText }),
      description: t("messages.confirm.statusChangeMessage", {
        status: statusText,
      }),
      buttons: [
        {
          label: t("common.cancel"),
          className: "custom-confirm-dialog__btn--cancel",
        },
        {
          label: t("common.confirm"),
          className: checked
            ? "custom-confirm-dialog__btn--success"
            : "custom-confirm-dialog__btn--warning",
          onClick: () => confirmStatusChange(id, newStatus),
        },
      ],
    });
  };

  const confirmStatusChange = async (id, newStatus) => {
    try {
      const response = await JournalistsServices.updateStatus(id, {
        status: newStatus,
      });
      if (response.success) {
        toast.success(t("journalists.statusChanged"));
        getData();
      } else {
        toast.error(response.message || t("messages.error.updateFailed"));
      }
    } catch {
      toast.error(t("messages.error.updateFailed"));
    }
  };

  useEffect(() => {
    if (currentJournalist) {
      setOpen(true);
      setTopicsPage(1); // Reset to first page when opening dialog
    }
  }, [currentJournalist]);

  // Fetch topics when currentJournalist or topicsPage changes
  useEffect(() => {
    let isMounted = true;
    const fetchTopics = async () => {
      if (!currentJournalist?.id) return;

      setLoadingTopics(true);
      try {
        const res = await TopicServices.getAll({
          limit: topicsLimit,
          page: topicsPage,
          contributor_id: currentJournalist.id,
        });
        if (isMounted && res?.success && res?.data) {
          setTopicsData(res.data.view.data || []);
          setTopicsTotalPages(res.data.view.totalPages || 1);
          setTopicsTotalItems(res.data.view.totalItems || 0);
        }
      } catch (e) {
        console.error("Failed to load topics", e);
      } finally {
        if (isMounted) {
          setLoadingTopics(false);
        }
      }
    };

    fetchTopics();
    return () => {
      isMounted = false;
    };
  }, [currentJournalist, topicsPage, topicsLimit]);

  // New functions for user type change dialog
  const handleOpenUserTypeDialog = (user) => {
    setUserToChangeType(user);
    setSelectedUserType(user.type || "");
    setUserTypeDialogOpen(true);
  };

  const handleCloseUserTypeDialog = () => {
    setUserTypeDialogOpen(false);
    setUserToChangeType(null);
    setSelectedUserType("");
  };

  const handleUserTypeChange = async () => {
    if (!userToChangeType || !selectedUserType) {
      toast.error(t("journalists.selectUserType"));
      return;
    }

    setUserTypeChangeLoading(true);
    try {
      const response = await JournalistsServices.update(userToChangeType.id, {
        type: selectedUserType,
      });
      if (response.success) {
        toast.success(t("journalists.userTypeChanged"));
        getData();
        handleCloseUserTypeDialog();
      } else {
        toast.error(response.message || t("messages.error.updateFailed"));
      }
    } catch {
      toast.error(t("messages.error.updateFailed"));
    } finally {
      setUserTypeChangeLoading(false);
    }
  };

  return (
    <div>
      <MainCard>
        {/* Header with Search and Filter Toggle */}
        <Box
          sx={{
            backgroundColor: theme.palette.background.paper,
            borderRadius: "14px",
            p: 2,
            mb: 2,
          }}
        >
          <Grid container spacing={2} alignItems="center">
            {/* Title */}
            <Grid
              item
              size={{ xs: 12, md: 3 }}
              display="flex"
              justifyContent={{ xs: "center", md: "flex-start" }}
            >
              <Typography
                variant="subtitle1"
                sx={{
                  color: theme.palette.text.primary,
                  fontWeight: 600,
                  fontSize: "1.5em",
                }}
              >
                {t("journalists.allJournalists")}
              </Typography>
            </Grid>

            {/* Search Bar */}
            <Grid
              item
              size={{ xs: 12, md: 7 }}
              display="flex"
              justifyContent="center"
            >
              <Paper
                elevation={0}
                sx={{
                  p: "2px 14px",
                  display: "flex",
                  alignItems: "center",
                  width: "100%",
                  maxWidth: 600,
                  border: `1.5px solid ${theme.palette.divider}`,
                  borderRadius: 3,
                  background: theme.palette.background.paper,
                  boxShadow: isLight
                    ? "0 2px 8px 0 rgba(2,89,165,0.05)"
                    : "0 3px 12px 0 rgba(0,188,212,0.08)",
                  transition: "border-color 0.18s, box-shadow 0.18s",
                  "&:hover, &:focus-within": {
                    borderColor: theme.palette.primary.light,
                    boxShadow: isLight
                      ? "0 4px 16px 0 rgba(2,89,165,0.10)"
                      : "0 6px 20px 0 rgba(0,188,212,0.14)",
                  },
                }}
              >
                <IconButton
                  sx={{ p: "10px", color: theme.palette.text.secondary }}
                  aria-label="menu"
                >
                  <MenuIcon />
                </IconButton>
                <InputBase
                  sx={{
                    ml: 1,
                    flex: 1,
                    fontSize: "1.05em",
                    color: theme.palette.text.primary,
                  }}
                  placeholder={t("journalists.searchJournalists")}
                  inputProps={{ "aria-label": "search users" }}
                  value={searchValue}
                  onChange={(e) => setSearchValue(e.target.value)}
                  onKeyPress={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault();
                      handleSearch();
                    }
                  }}
                />
                <IconButton
                  sx={{ p: "10px", color: theme.palette.primary.main }}
                  aria-label="search"
                  onClick={(e) => {
                    e.preventDefault();
                    handleSearch();
                  }}
                  disabled={loading}
                >
                  {loading ? (
                    <CircularProgress size={20} color="primary" />
                  ) : (
                    <SearchIcon />
                  )}
                </IconButton>
              </Paper>
            </Grid>

            {/* Filter Toggle Button */}
            <Grid
              item
              size={{ xs: 12, md: 2 }}
              display="flex"
              justifyContent={{ xs: "center", md: "flex-end" }}
            >
              <Tooltip title={t("common.filter")}>
                <IconButton
                  onClick={() => setFilterView(!filterView)}
                  sx={{
                    backgroundColor: filterView
                      ? alpha(theme.palette.primary.main, 0.1)
                      : "transparent",
                    color: filterView
                      ? theme.palette.primary.main
                      : theme.palette.text.secondary,
                    border: `1.5px solid ${
                      filterView
                        ? theme.palette.primary.main
                        : theme.palette.divider
                    }`,
                    borderRadius: 2,
                    padding: "10px",
                    transition: "all 0.2s",
                    "&:hover": {
                      backgroundColor: alpha(theme.palette.primary.main, 0.15),
                      borderColor: theme.palette.primary.main,
                    },
                  }}
                >
                  <FilterAltOutlinedIcon />
                </IconButton>
              </Tooltip>
            </Grid>

            {/* Filter Section */}
            {filterView && (
              <>
                <Grid item size={{ xs: 12 }}>
                  <Divider />
                </Grid>
                <Grid item size={{ xs: 12 }}>
                  <Typography>{t("journalists.filterByStatus")}</Typography>
                </Grid>

                <Grid item size={{ xs: 12 }}>
                  <ValidatorForm onSubmit={() => {}} process={false}>
                    <Grid container spacing={2}>
                      <Grid item size={{ xs: 12, md: 4 }}>
                        <Autocomplete
                          name={"status"}
                          label={t("common.status")}
                          value={selectedStatus}
                          onChange={handleStatusChange}
                          options={[
                            { label: t("common.active"), value: "Active" },
                            { label: t("common.inactive"), value: "Inactive" },
                          ]}
                          getOptionLabel={(option) => option.label || ""}
                          placeholder={t("common.status")}
                        />
                      </Grid>
                      <Grid item size={{ xs: 12, md: 4 }}>
                        <Autocomplete
                          name={"type"}
                          label={t("journalists.type")}
                          value={selectedType}
                          onChange={handleTypeChange}
                          options={[
                            {
                              label: t("journalists.journalist"),
                              value: "journalist",
                            },
                            {
                              label: t("journalists.informant"),
                              value: "informant",
                            },
                            { label: t("journalists.user"), value: "user" },
                          ]}
                          getOptionLabel={(option) => option.label || ""}
                          placeholder={t("journalists.type")}
                        />
                      </Grid>
                      <Grid
                        item
                        size={{ xs: 12, md: 4 }}
                        alignItems={"flex-end"}
                      >
                        <Box
                          display="flex"
                          gap={1}
                          alignItems="flex-end"
                          height="100%"
                        >
                          <Button
                            startIcon={<FilterAltOffIcon />}
                            size="large"
                            onClick={handleClearFilters}
                            variant="outlined"
                            fullWidth
                          >
                            {t("topics.clearFilters")}
                          </Button>
                        </Box>
                      </Grid>
                    </Grid>
                  </ValidatorForm>
                </Grid>
              </>
            )}
          </Grid>
        </Box>

        {/* Table Section */}
        <CollapsibleSection showButton={false} />
        <div className="mt-5">
          <MuiTable
            columns={[
              {
                accessorKey: "actions",
                header: t("common.actions"),
                Cell: ({ cell }) => {
                  const rowData = cell.row.original;
                  return (
                    <div className="flex flex-column">
                      <div className="mb-1 flex items-center gap-1 justify-center">
                        <Tooltip title={t("journalists.viewJournalist")}>
                          <IconButton
                            size="small"
                            color="primary"
                            onClick={() => {
                              setCurrentJournalist(rowData);
                            }}
                          >
                            <VisibilityIcon />
                          </IconButton>
                        </Tooltip>

                        <Tooltip title={t("common.status")}>
                          <Switch
                            size="small"
                            checked={rowData.status === "Active"}
                            onChange={async (_e, checked) => {
                              await changeStatus(rowData.id, checked);
                            }}
                          />
                        </Tooltip>

                        <Tooltip title={t("journalists.deleteJournalist")}>
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() => {
                              handleOpenDialog(rowData.id);
                            }}
                          >
                            <Delete />
                          </IconButton>
                        </Tooltip>
                      </div>
                      <Button
                        variant="contained"
                        size="small"
                        type="button"
                        onClick={() => handleOpenUserTypeDialog(rowData)}
                      >
                        {t("journalists.changeUserType")}
                      </Button>
                    </div>
                  );
                },
              },
              {
                accessorKey: "name",
                header: t("common.name"),
              },
              {
                accessorKey: "email",
                header: t("auth.email"),
              },
              {
                accessorKey: "contact_no",
                header: t("profile.phone"),
                Cell: ({ cell }) => {
                  return <span>{cell.getValue() ? cell.getValue() : "-"}</span>;
                },
              },

              {
                accessorKey: "createdAt",
                header: t("profile.joinDate"),
                Cell: ({ cell }) => {
                  return <span>{dateParse(cell.getValue())}</span>;
                },
              },
              {
                accessorKey: "updatedAt",
                header: t("common.updatedAt"),
                Cell: ({ cell }) => {
                  return <span>{dateParse(cell.getValue())}</span>;
                },
              },
              {
                accessorKey: "type",
                header: t("journalists.type"),
              },
              {
                accessorKey: "status",
                header: t("common.status"),
                Cell: ({ cell }) => {
                  const status = cell.getValue();
                  return (
                    <StatusTag status={status.toLowerCase()} variant="small" />
                  );
                },
              },
            ]}
            data={data}
            title="DLIs"
            manualPagination={true}
            rowCount={totalItems}
            currentPage={filters.page}
            pageSize={filters.limit}
            onPageChange={setPage}
            onPageSizeChange={setPageSize}
            loading={loading}
          />
        </div>
      </MainCard>

      {/* Details Dialog with stats and contributions */}
      <CustomDialog
        open={open}
        onClose={() => setOpen(false)}
        title={t("journalists.userDetails")}
        maxWidth="xl"
      >
        <Box sx={{ p: { xs: 1, sm: 2 } }}>
          <Grid container spacing={3}>
            {/* Modern Profile Header */}
            <Grid item size={{ xs: 12 }}>
              <Box
                sx={{
                  background: `linear-gradient(135deg, ${alpha(
                    theme.palette.primary.main,
                    0.08
                  )} 0%, ${alpha(theme.palette.secondary.main, 0.05)} 100%)`,
                  borderRadius: 4,
                  p: 3,
                  border: `1px solid ${alpha(
                    theme.palette.primary.main,
                    0.12
                  )}`,
                  boxShadow: isLight
                    ? `0 4px 20px ${alpha(theme.palette.primary.main, 0.08)}`
                    : `0 4px 20px ${alpha(theme.palette.secondary.main, 0.15)}`,
                }}
              >
                <Grid container spacing={3} alignItems="center">
                  <Grid item>
                    <Avatar
                      alt={currentJournalist?.name || "Profile"}
                      src={
                        currentJournalist?.avatar ||
                        "/static/images/avatar/1.jpg"
                      }
                      sx={{
                        width: { xs: 80, sm: 120 },
                        height: { xs: 80, sm: 120 },
                        border: `3px solid ${theme.palette.background.paper}`,
                        boxShadow: isLight
                          ? `0 4px 16px ${alpha(
                              theme.palette.primary.main,
                              0.15
                            )}`
                          : `0 4px 16px ${alpha(
                              theme.palette.secondary.main,
                              0.2
                            )}`,
                      }}
                    />
                  </Grid>
                  <Grid item xs>
                    <Typography
                      variant="h5"
                      sx={{
                        fontWeight: 700,
                        mb: 1,
                        color: theme.palette.text.primary,
                      }}
                    >
                      {currentJournalist?.name || "User"}
                    </Typography>
                    <Stack spacing={1.5}>
                      <Stack direction="row" spacing={1} alignItems="center">
                        <EmailOutlinedIcon
                          sx={{
                            fontSize: 18,
                            color: theme.palette.text.secondary,
                          }}
                        />
                        <Typography variant="body2" color="text.secondary">
                          {currentJournalist?.email || "-"}
                        </Typography>
                      </Stack>
                      {currentJournalist?.mobile && (
                        <Stack direction="row" spacing={1} alignItems="center">
                          <PhoneOutlinedIcon
                            sx={{
                              fontSize: 18,
                              color: theme.palette.text.secondary,
                            }}
                          />
                          <Typography variant="body2" color="text.secondary">
                            {currentJournalist?.mobile}
                          </Typography>
                        </Stack>
                      )}
                      <Stack direction="row" spacing={1} alignItems="center">
                        <CalendarTodayOutlinedIcon
                          sx={{
                            fontSize: 18,
                            color: theme.palette.text.secondary,
                          }}
                        />
                        <Typography variant="body2" color="text.secondary">
                          {t("profile.joinDate")}:{" "}
                          {dateTimeParse(currentJournalist?.createdAt)}
                        </Typography>
                      </Stack>
                    </Stack>
                  </Grid>
                </Grid>
              </Box>
            </Grid>

            {/* Stats Cards */}
            {/* <Grid item size={{ xs: 12 }}>
              <Grid container spacing={2}>
                <Grid item size={{ xs: 12, sm: 6 }}>
                  <SummaryCard
                    icon={<ArticleOutlinedIcon sx={{ fontSize: 28 }} />}
                    label="Total Contributions"
                    value={totalContrib}
                  />
                </Grid>
                <Grid item size={{ xs: 12, sm: 6 }}>
                  <SummaryCard
                    icon={<TodayOutlinedIcon sx={{ fontSize: 28 }} />}
                    label="This Month"
                    value={thisMonthContrib}
                  />
                </Grid>
              </Grid>
            </Grid> */}

            {/* Topics Section */}
            <Grid item size={{ xs: 12 }}>
              <Box
                sx={{
                  mt: 2,
                  mb: 1,
                }}
              >
                <Stack
                  direction="row"
                  alignItems="center"
                  justifyContent="space-between"
                  sx={{ mb: 3 }}
                >
                  <Typography
                    variant="h6"
                    sx={{
                      fontWeight: 700,
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                    }}
                  >
                    <ArticleOutlinedIcon />
                    {t("journalists.topicsContributed")}
                  </Typography>
                  <Chip
                    label={`${topicsTotalItems} ${t(
                      "topics.allTopics"
                    ).toLowerCase()}`}
                    size="small"
                    color="primary"
                    variant="outlined"
                  />
                </Stack>

                {loadingTopics ? (
                  <Box
                    display="flex"
                    justifyContent="center"
                    alignItems="center"
                    minHeight={250}
                  >
                    <CircularProgress size={50} />
                  </Box>
                ) : topicsData.length === 0 ? (
                  <Box
                    sx={{
                      p: 5,
                      borderRadius: 4,
                      textAlign: "center",
                      border: `2px dashed ${alpha(
                        theme.palette.text.secondary,
                        0.2
                      )}`,
                      background: alpha(theme.palette.background.paper, 0.4),
                    }}
                  >
                    <ArticleOutlinedIcon
                      sx={{
                        fontSize: 60,
                        color: theme.palette.text.disabled,
                        mb: 2,
                      }}
                    />
                    <Typography
                      variant="h6"
                      color="text.secondary"
                      gutterBottom
                    >
                      {t("topics.noTopicsFound")}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {t("journalists.contributedTopics")}
                    </Typography>
                  </Box>
                ) : (
                  <>
                    <Grid container spacing={2}>
                      {topicsData.map((topic) => (
                        <Grid
                          key={topic?.id}
                          item
                          size={{ xs: 12, sm: 6, lg: 3 }}
                        >
                          <InfoRequestCard {...topic} />
                        </Grid>
                      ))}
                    </Grid>
                    {topicsTotalPages > 1 && (
                      <Stack
                        direction="row"
                        justifyContent="center"
                        sx={{ mt: 4 }}
                      >
                        <Pagination
                          count={topicsTotalPages}
                          page={topicsPage}
                          onChange={(event, value) => setTopicsPage(value)}
                          color="primary"
                          shape="rounded"
                          size="large"
                          showFirstButton
                          showLastButton
                          sx={{
                            "& .MuiPaginationItem-root": {
                              borderRadius: 2,
                              fontWeight: 500,
                              fontSize: "0.95rem",
                              minWidth: 40,
                              height: 40,
                              border: `1.5px solid ${
                                isLight
                                  ? "rgba(2,89,165,0.12)"
                                  : "rgba(0,188,212,0.18)"
                              }`,
                              background: isLight
                                ? "rgba(255,255,255,0.7)"
                                : "rgba(34,45,59,0.6)",
                              transition: "all 0.2s",
                              "&:hover": {
                                background: isLight
                                  ? "rgba(2,89,165,0.08)"
                                  : "rgba(0,188,212,0.12)",
                                transform: "scale(1.05)",
                              },
                              "&.Mui-selected": {
                                background: isLight
                                  ? "rgba(2,89,165,0.15)"
                                  : "rgba(0,188,212,0.20)",
                                color: theme.palette.primary.main,
                                fontWeight: 700,
                                border: `1.5px solid ${theme.palette.primary.main}`,
                                "&:hover": {
                                  background: isLight
                                    ? "rgba(2,89,165,0.22)"
                                    : "rgba(0,188,212,0.28)",
                                },
                              },
                            },
                          }}
                        />
                      </Stack>
                    )}
                  </>
                )}
              </Box>
            </Grid>
          </Grid>
        </Box>
      </CustomDialog>

      {/* User Type Change Dialog */}
      <CustomDialog
        open={userTypeDialogOpen}
        onClose={handleCloseUserTypeDialog}
        title={t("journalists.changeUserType")}
        maxWidth="sm"
      >
        <DialogContent sx={{ p: 0, m: 0 }}>
          <Box>
            <Typography variant="body1" sx={{ mb: 3 }}>
              {t("journalists.changeUserType")}:{" "}
              <strong>{userToChangeType?.name}</strong>
            </Typography>

            <FormControl fullWidth>
              <InputLabel id="user-type-select-label">
                {t("journalists.type")}
              </InputLabel>
              <Select
                labelId="user-type-select-label"
                value={selectedUserType}
                label={t("journalists.type")}
                onChange={(e) => setSelectedUserType(e.target.value)}
              >
                <MenuItem value="journalist">
                  {t("journalists.journalist")}
                </MenuItem>
                <MenuItem value="informant">
                  {t("journalists.informant")}
                </MenuItem>
                <MenuItem value="user">{t("journalists.user")}</MenuItem>
              </Select>
            </FormControl>
          </Box>
        </DialogContent>

        <DialogActions sx={{ p: 3, pt: 2 }}>
          <Button onClick={handleCloseUserTypeDialog} variant="outlined">
            {t("common.cancel")}
          </Button>
          <Button
            onClick={handleUserTypeChange}
            variant="contained"
            disabled={userTypeChangeLoading || !selectedUserType}
          >
            {userTypeChangeLoading ? t("common.loading") : t("common.save")}
          </Button>
        </DialogActions>
      </CustomDialog>
    </div>
  );
};

export default AllJournalists;
